import React, { useRef, useState, useCallback, memo } from 'react';
import CustomDropdown from '../../components/ui/CustomDropdown';
import CountryCodeDropdown from '../../components/ui/CountryCodeDropdown';
import SingleDatePicker from '../../components/common/SingleDatePicker';
import { countryNameToCode } from '../../utils/countryCodeUtils';
import { toastService } from '../../services/toastService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { UploadIcon, CheckIcon } from '../../components/Icons/svgIcons';
import SignatureField from './SignatureField';
import TimePicker from '../../components/ui/TimePicker';
import AudioWidget from '../../components/ui/AudioWidget';
import GeoWidget from '../../components/ui/GeoWidget';
import NoteWidget from '../../components/ui/NoteWidget';
import CalculateWidget from '../../components/ui/CalculateWidget';

const countryCodeOptions = Object.entries(countryNameToCode).map(([name, code]) => ({
  value: code,
  label: `${name.charAt(0).toUpperCase() + name.slice(1)} (${code})`,
  country: name,
  code: code
}));

const FileUploadField = memo(({ 
  value, 
  onChange,  
  uploadStatus, 
  setUploadStatus, 
  multiple, 
  isImage,
  fieldId
}) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isPWA] = useState(() => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);
  const isUploading = uploadStatus === 'uploading';

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  };

  const processFiles = useCallback((selectedFiles) => {
    if (!selectedFiles?.length) return;
    if (setUploadStatus) setUploadStatus('uploading');
    
    const filesToProcess = multiple ? selectedFiles : [selectedFiles[0]];

    const processPromises = filesToProcess.map(file => {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toastService.error(`File "${file.name}" exceeds 10MB limit.`);
        return Promise.resolve(null);
      }
      return Promise.resolve(file);
    });

    Promise.all(processPromises)
      .then(processedFiles => {
        const validFiles = processedFiles.filter(Boolean);
        if (validFiles.length === 0) {
          if (setUploadStatus) setUploadStatus('idle');
          return;
        }
        
        if (multiple) {
          const currentFiles = Array.isArray(value) ? value : [];
          onChange([...currentFiles, ...validFiles]);
        } else {
          onChange(validFiles[0]);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (setUploadStatus) setUploadStatus('idle');
      });
  }, [multiple, onChange, setUploadStatus, value]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
    processFiles(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  };

  const handlePWAUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = isImage ? 'image/*' : '*/*';
    input.multiple = multiple;
    input.style.display = 'none';
    document.body.appendChild(input);

    const cleanup = () => {
      if (input.parentNode) input.parentNode.removeChild(input);
    };

    input.addEventListener('change', (e) => {
      const selectedFiles = Array.from(e.target.files);
      cleanup();
      processFiles(selectedFiles);
    }, { once: true });

    input.addEventListener('cancel', cleanup, { once: true });
    setTimeout(() => input.click(), 100);
  };

  const handleUploadClick = () => {
    if (isPWA) {
      handlePWAUpload();
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = (index) => {
    if (multiple) {
      const currentFiles = Array.isArray(value) ? value : [];
      const newFiles = currentFiles.filter((_, i) => i !== index);
      onChange(newFiles.length > 0 ? newFiles : []);
    } else {
      onChange(null);
    }
  };

  const files = Array.isArray(value) ? value : value ? [value] : [];
  const hasFiles = files.length > 0;

  const getFileDisplayName = (file) => {
    if (file instanceof File || file instanceof Blob) return file.name || 'Unknown file';
    if (typeof file === 'string') {
      try {
        // Strip out UUID stuff if possible, or just take the end
        const url = new URL(file);
        const name = url.pathname.split('/').pop();
        if (name && name.length > 30) {
          return `${name.substring(0, 15)}...${name.substring(name.length - 10)}`;
        }
        return name || 'Server file';
      } catch {
        const name = file.split('/').pop();
        if (name && name.length > 30) {
           return `${name.substring(0, 15)}...${name.substring(name.length - 10)}`;
        }
        return name || 'Server file';
      }
    }
    return 'Unknown file';
  };

  const getFilePreview = (file) => {
    if (file instanceof File || file instanceof Blob) {
      if (file.type?.startsWith('image/')) return URL.createObjectURL(file);
      return null;
    }
    if (typeof file === 'string') {
      const isLikelyImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)(\?.*)?$/i.test(file);
      if (isLikelyImage || isImage) return file;
    }
    return null;
  };

  const getFileSizeDisplay = (file) => {
    if (file instanceof File || file instanceof Blob) return formatFileSize(file.size || 0);
    if (typeof file === 'string') {
      const ext = file.split('.').pop()?.split('?')[0]?.toUpperCase();
      return ext ? `${ext} Image (Server)` : 'Server File';
    }
    return '0 Bytes';
  };

  return (
    <div className="space-y-3">
      {!isPWA && (
        <input
          ref={fileInputRef}
          id={`file-${fieldId}`}
          type="file"
          accept={isImage ? 'image/*' : '*/*'}
          multiple={multiple}
          onChange={handleFileChange}
          className="hidden"
        />
      )}

      {hasFiles && (
        <div className="space-y-2.5">
          {files.map((file, index) => {
            const preview = getFilePreview(file);
            const isImageFile = (file instanceof File || file instanceof Blob) && file.type?.startsWith('image/');

            return (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-3.5 shadow-sm flex items-center justify-between hover:border-[var(--btn-primary-bg)] transition-all duration-200">
                <div className="flex items-center gap-3 overflow-hidden">
                  {preview ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-14 h-14 object-cover rounded-lg flex-shrink-0 border border-gray-200"
                      onLoad={(e) => {
                        if (isImageFile) URL.revokeObjectURL(e.target.src);
                      }}
                    />
                  ) : (
                    <div className="w-14 h-14 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200">
                      <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate mb-0.5">{getFileDisplayName(file)}</p>
                    <p className="text-xs text-gray-500 font-medium">{getFileSizeDisplay(file)}</p>
                  </div>
                </div>
                <button 
                  type="button" 
                  onClick={() => handleRemoveFile(index)} 
                  className="text-gray-400 hover:text-red-600 p-2.5 transition-all duration-200 hover:bg-red-50 rounded-lg active:scale-95 outline-none focus:outline-none focus-visible:outline-none"
                  aria-label="Remove file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {(!hasFiles || multiple) && (
        <div className="space-y-2.5">
          <button
            type="button"
            id={`upload-${fieldId}`}
            onClick={handleUploadClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={isUploading}
            className={`flex flex-col items-center justify-center gap-3 w-full border-2 border-dashed rounded-lg p-8 transition-all duration-200 outline-none focus:outline-none focus-visible:outline-none ${
              isUploading || isDragOver
                ? 'border-[var(--btn-primary-bg)] bg-[var(--btn-primary-bg)]/8'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 active:scale-[0.99]'
            }`}
          >
            {isUploading ? <LoadingSpinner /> : <UploadIcon className="w-11 h-11 text-gray-400" />}
            <span className={`text-sm font-bold transition-colors ${isDragOver ? 'text-[var(--btn-primary-bg)]' : 'text-gray-700'}`}>
              {isUploading ? 'Uploading...' : isDragOver ? 'Drop files here' : `Click to upload ${multiple ? 'files' : 'file'}`}
            </span>
            <p className="text-xs text-gray-500 text-center font-medium">
              {isUploading 
                ? 'Please wait...' 
                : isDragOver 
                  ? 'Release to upload' 
                  : `${isImage ? 'Images (max 10MB)' : 'Any file (max 10MB)'}`
              }
            </p>
          </button>
          <div className="flex justify-end">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg ${multiple ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
              {multiple ? 'Multiple Files Allowed' : 'Single File Only'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

const ReadOnlyMediaBanner = memo(({ fieldType, value }) => {
  const hasExistingValue = value !== null && value !== undefined && value !== '' && 
    (!Array.isArray(value) || value.length > 0);
  
  const typeLabels = {
    'FILE': 'File', 'MULTI-FILE': 'File', 'FILE_MULTIPLE': 'File',
    'IMAGE': 'Image', 'VIDEO': 'Video', 'AUDIO': 'Audio Recording',
    'DOCUMENT': 'Document', 'SIGNATURE': 'Signature',
    'GEOPOINT': 'GPS Location', 'GEOTRACE': 'Geo Trace', 'GEOSHAPE': 'Geo Shape'
  };
  const label = typeLabels[fieldType] || 'Media';

  return (
    <div className="space-y-2.5">
      {/* Info Banner */}
      <div className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
        <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-amber-800">{label} fields cannot be modified</p>
          <p className="text-[11px] text-amber-600 mt-0.5 leading-relaxed">To change this field, please create a new submission.</p>
        </div>
      </div>
      {/* Show existing value preview */}
      {hasExistingValue && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 opacity-60 pointer-events-none">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-xs text-gray-500 font-medium truncate">Existing {label.toLowerCase()} attached</span>
          </div>
        </div>
      )}
    </div>
  );
});
ReadOnlyMediaBanner.displayName = 'ReadOnlyMediaBanner';

const FieldRenderer = React.memo(({ field, value, onChange, uploadStatus, setUploadStatus, nameValues = {}, error, hasAttemptedSubmit, readOnly }) => {
  const [showSuccessFlash, setShowSuccessFlash] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const prevErrorRef = useRef(error);

  React.useEffect(() => {
    // If error was truthy and now it's falsy, trigger flash
    if (prevErrorRef.current && !error) {
      if (prevErrorRef.current.type === 'constraint') {
        setShowSuccessFlash(true);
        const timer = setTimeout(() => setShowSuccessFlash(false), 1000);
        return () => clearTimeout(timer);
      }
    }
    prevErrorRef.current = error;
  }, [error, hasAttemptedSubmit]);

  const fieldName = field.id || field.name || `field-${Math.random()}`;
  const fieldId = `field-${fieldName}`;
  const fieldType = field.type?.toUpperCase();
  const isPassword = fieldType === 'PASSWORD';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : fieldType?.toLowerCase();

  switch (fieldType) {
    case 'NOTE':
      return <NoteWidget field={field} nameValues={nameValues} />;

    case 'CALCULATE':
      return <CalculateWidget field={field} value={value} />;

    case 'TEXT':
    case 'EMAIL':
    case 'PASSWORD':
    case 'URL': {
      return (
        <div className="relative group/input">
          <input
            id={fieldId}
            type={inputType}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || 'value'}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
            disabled={readOnly}
            minLength={isPassword ? 6 : undefined}
            autoComplete={fieldType === 'EMAIL' ? 'email' : isPassword ? 'new-password' : 'url'}
            style={{ outline: 'none', boxShadow: 'none' }}
            className={`w-full h-12 px-4 ${isPassword ? 'pr-12' : ''} border rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 transition-all duration-300 [outline:none] focus:[outline:none] focus-visible:[outline:none] [box-shadow:none] focus:[box-shadow:none] ${
              readOnly ? 'bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed' :
              showSuccessFlash 
                ? 'border-green-400 bg-green-50/10 text-green-600 focus:bg-green-50/10' 
                : error && (error.type !== 'required' || hasAttemptedSubmit)
                  ? 'border-red-500 bg-white focus:border-red-500 focus:bg-white'
                  : 'border-gray-300 hover:border-gray-400 focus:border-[var(--btn-primary-bg)] focus:bg-[var(--btn-primary-bg)]/3'
            }`}
          />
          {isPassword && (
            <button
              type="button"
              onMouseDown={() => setShowPassword(true)}
              onMouseUp={() => setShowPassword(false)}
              onMouseLeave={() => setShowPassword(false)}
              onTouchStart={() => setShowPassword(true)}
              onTouchEnd={() => setShowPassword(false)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 active:scale-90 transition-all outline-none focus:outline-none"
              tabIndex="-1"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          )}
        </div>
      );
    }

    case 'NUMBER':
    case 'INTEGER':
    case 'DECIMAL':
      return (
        <input
          id={fieldId}
          type="number"
          placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || 'value'}`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
          style={{ outline: 'none', boxShadow: 'none' }}
          className={`w-full h-12 px-4 border rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 transition-all duration-300 [outline:none] focus:[outline:none] focus-visible:[outline:none] [box-shadow:none] focus:[box-shadow:none] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            readOnly ? 'bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed' :
            showSuccessFlash 
              ? 'border-green-400 bg-green-50/10 text-green-600 focus:bg-green-50/10' 
              : error && (error.type !== 'required' || hasAttemptedSubmit)
                ? 'border-red-500 bg-white focus:border-red-500 focus:bg-white'
                : 'border-gray-300 hover:border-gray-400 focus:border-[var(--btn-primary-bg)] focus:bg-[var(--btn-primary-bg)]/3'
          }`}
        />
      );

  case 'TEL':
  case 'PHONENUMBER': {
        const matchingCode = countryCodeOptions.find(c => String(value).startsWith(c.value));
        const selectedCountryCode = matchingCode ? matchingCode.value : '+91';
        const phoneNumber = value ? String(value).substring(selectedCountryCode.length) : '';

        const handleCodeChange = (newCode) => {
          const currentNumber = phoneNumber.replace(/\D/g, '');
          onChange(`${newCode}${currentNumber}`);
        };

        const handleNumberChange = (e) => {
          const digits = e.target.value.replace(/\D/g, '');
          const limitedDigits = digits.slice(0, 15);
          onChange(`${selectedCountryCode}${limitedDigits}`);
        };

        return (
          <div id={fieldId} className={`flex h-12 w-full border rounded-lg bg-white transition-all duration-300 relative ${
            showSuccessFlash 
              ? 'border-green-400 bg-green-50/10' 
              : error && (error.type !== 'required' || hasAttemptedSubmit)
                ? 'border-red-500 bg-white focus-within:border-red-500 focus-within:bg-white'
                : 'border-gray-300 hover:border-gray-400 focus-within:border-[var(--btn-primary-bg)] focus-within:bg-[var(--btn-primary-bg)]/3'
          }`}>
            <div className="flex-shrink-0 w-20 border-r border-gray-300">
              <CountryCodeDropdown
                value={selectedCountryCode}
                onChange={handleCodeChange}
                options={countryCodeOptions}
                className="h-full"
              />
            </div>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="tel-national"
              placeholder={field.placeholder || 'Enter phone number'}
              value={phoneNumber}
              onChange={handleNumberChange}
              readOnly={readOnly}
              disabled={readOnly}
              maxLength={15}
              style={{ outline: 'none', boxShadow: 'none' }}
              className={`flex-1 h-full px-4 border-none bg-transparent text-sm text-gray-900 placeholder-gray-400 [outline:none] focus:[outline:none] focus-visible:[outline:none] [box-shadow:none] focus:[box-shadow:none] rounded-r-lg ${readOnly ? 'cursor-not-allowed text-gray-500' : ''}`}
            />
          </div>
        );
      }

    case 'TEXTAREA':
      return (
        <textarea
          id={fieldId}
          placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || 'details'}`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
          rows={5}
          style={{ outline: 'none', boxShadow: 'none' }}
          className={`w-full px-4 py-3 border rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 transition-all duration-300 resize-y min-h-[120px] [outline:none] focus:[outline:none] focus-visible:[outline:none] [box-shadow:none] focus:[box-shadow:none] ${
            readOnly ? 'bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed' :
            showSuccessFlash 
              ? 'border-green-400 bg-green-50/10 text-green-600 focus:bg-green-50/10' 
              : error && (error.type !== 'required' || hasAttemptedSubmit)
                ? 'border-red-500 bg-white focus:border-red-500 focus:bg-white'
                : 'border-gray-300 hover:border-gray-400 focus:border-[var(--btn-primary-bg)] focus:bg-[var(--btn-primary-bg)]/3'
          }`}
        />
      );

    case 'DATE': {
      const currentYear = new Date().getFullYear();
      const minDate = new Date(currentYear - 150, 0, 1);
      return (
        <div id={fieldId} className="relative z-10">
          <SingleDatePicker
            value={value}
            onChange={onChange}
            placeholder="dd-mm-yyyy"
            minDate={minDate}
            isRequired={field.is_required}
          />
        </div>
      );
    }

    case 'TIME':
      return <TimePicker value={value} onChange={onChange} placeholder={field.placeholder || "Select time"} />;

    case 'DATETIME':
      return (
        <div className="space-y-3" id={fieldId}>
          <SingleDatePicker 
            value={value?.split('T')[0] || value} 
            onChange={(date) => {
              const time = value?.split('T')[1] || '00:00';
              onChange(date ? `${date}T${time}` : '');
            }} 
            placeholder="Select date" 
          />
          <TimePicker 
            value={value?.split('T')[1] || ''} 
            onChange={(time) => {
              const date = value?.split('T')[0] || new Date().toISOString().split('T')[0];
              onChange(time ? `${date}T${time}` : '');
            }} 
            placeholder="Select time" 
          />
        </div>
      );

    case 'AUDIO':
      if (readOnly) return <ReadOnlyMediaBanner fieldType={fieldType} value={value} />;
      return <AudioWidget value={value} onChange={onChange} label={field.label} />;

    case 'GEOPOINT':
    case 'GEOTRACE':
    case 'GEOSHAPE':
      if (readOnly) return <ReadOnlyMediaBanner fieldType={fieldType} value={value} />;
      return <GeoWidget value={value} onChange={onChange} type={fieldType.toLowerCase()} />;

    case 'RADIO':
    case 'SELECT':
    case 'SELECT_ONE':
    case 'SELECT-ONE': {
      const options = Array.isArray(field.options) ? field.options : [];
      return (
        <div className="space-y-2.5" role="radiogroup" aria-labelledby={fieldId}>
          {options.map((option, index) => {
            const optionId = `${fieldId}-option-${index}`;
            const isSelected = String(value) === String(option.value);
            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className={`flex items-center gap-3 cursor-pointer px-4 py-3.5 rounded-lg border transition-all duration-200 ${
                  isSelected
                    ? 'border-[var(--btn-primary-bg)] bg-[var(--btn-primary-bg)]/8'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                  isSelected ? 'border-[var(--btn-primary-bg)]' : 'border-gray-400'
                }`}>
                  {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[var(--btn-primary-bg)]" />}
                </div>
                <span className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                  {option.label || option.value}
                </span>
                <input
                  id={optionId}
                  type="radio"
                  name={fieldName}
                  value={option.value}
                  checked={isSelected}
                  onChange={() => !readOnly && onChange(option.value)}
                  disabled={readOnly}
                  className="sr-only"
                />
              </label>
            );
          })}
        </div>
      );
    }

    case 'CHECKBOX':
    case 'MULTI-SELECT':
    case 'MULTI_SELECT':
    case 'SELECT-MULTIPLE':
    case 'SELECT_MULTIPLE': {
      const options = Array.isArray(field.options) ? field.options : [];
      const selectedValues = Array.isArray(value) ? value.map(String) : [];

      const handleCheckboxChange = (optionVal) => {
        const strVal = String(optionVal);
        const newValues = selectedValues.includes(strVal)
          ? selectedValues.filter(v => v !== strVal)
          : [...selectedValues, strVal];
        onChange(newValues);
      };

      return (
        <div className="space-y-2.5" role="group" aria-labelledby={fieldId}>
          {options.map((option, index) => {
            const isChecked = selectedValues.includes(String(option.value));
            const optionId = `${fieldId}-option-${index}`;
            return (
              <label
                key={option.value}
                htmlFor={optionId}
                className={`flex items-center gap-3 cursor-pointer px-4 py-3.5 rounded-lg border transition-all duration-200 ${
                  isChecked
                    ? 'border-[var(--btn-primary-bg)] bg-[var(--btn-primary-bg)]/8'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                  isChecked ? 'border-[var(--btn-primary-bg)] bg-[var(--btn-primary-bg)]' : 'border-gray-400 bg-white'
                }`}>
                  {isChecked && <CheckIcon className="w-3.5 h-3.5 text-white" />}
                </div>
                <span className={`text-sm font-medium ${isChecked ? 'text-gray-900' : 'text-gray-600'}`}>
                  {option.label || option.value}
                </span>
                <input
                  id={optionId}
                  type="checkbox"
                  name={fieldName}
                  checked={isChecked}
                  onChange={() => !readOnly && handleCheckboxChange(option.value)}
                  disabled={readOnly}
                  className="sr-only"
                />
              </label>
            );
          })}
        </div>
      );
    }

    case 'DROPDOWN':
    case 'MULTIDROP':
    case 'MULTI-DROPDOWN': {
      const isMultiple = fieldType === 'MULTIDROP' || fieldType === 'MULTI-DROPDOWN';
      const options = Array.isArray(field.options) 
        ? field.options.map(opt => ({
            value: opt.value,
            label: opt.label || opt.value
          }))
        : [];
      return (
        <div id={fieldId}>
          <CustomDropdown
            value={value}
            onChange={onChange}
            options={options}
            placeholder={field.placeholder || 'Select an option'}
            multiple={isMultiple}
            disabled={readOnly}
          />
        </div>
      );
    }

    case 'FILE':
    case 'MULTI-FILE':
    case 'FILE_MULTIPLE':
    case 'IMAGE':
    case 'VIDEO': {
      if (readOnly) return <ReadOnlyMediaBanner fieldType={fieldType} value={value} />;
      const isImage = fieldType === 'IMAGE';
      const isMultiFile = fieldType === 'MULTI-FILE';
      const multiple = isMultiFile || (field.multiple === true && fieldType !== 'FILE');
      
      return (
        <FileUploadField
          fieldId={fieldId}
          value={value}
          onChange={onChange}
          field={field}
          uploadStatus={uploadStatus}
          setUploadStatus={setUploadStatus}
          multiple={multiple}
          isImage={isImage}
        />
      );
    }

    case 'DOCUMENT': {
      if (readOnly) return <ReadOnlyMediaBanner fieldType={fieldType} value={value} />;
      const multiple = field.multiple === true;
      
      return (
        <FileUploadField
          fieldId={fieldId}
          value={value}
          onChange={onChange}
          field={field}
          uploadStatus={uploadStatus}
          setUploadStatus={setUploadStatus}
          multiple={multiple}
          isImage={false}
        />
      );
    }

    case 'SIGNATURE':
      if (readOnly) return <ReadOnlyMediaBanner fieldType={fieldType} value={value} />;
      return (
        <SignatureField
          fieldId={fieldId}
          value={value}
          onChange={onChange}
        />
      );

    default:
      return (
        <input
          id={fieldId}
          type="text"
          placeholder={field.placeholder || 'Enter value'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ outline: 'none', boxShadow: 'none' }}
          className={`w-full h-12 px-4 border rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 transition-all duration-300 [outline:none] focus:[outline:none] focus-visible:[outline:none] [box-shadow:none] focus:[box-shadow:none] ${
            showSuccessFlash 
              ? 'border-green-400 bg-green-50/10 text-green-600 focus:bg-green-50/10' 
              : error && (error.type !== 'required' || hasAttemptedSubmit)
                ? 'border-red-500 bg-white focus:border-red-500 focus:bg-white'
                : 'border-gray-300 hover:border-gray-400 focus:border-[var(--btn-primary-bg)] focus:bg-[var(--btn-primary-bg)]/3'
          }`}
        />
      );
  }
});

FieldRenderer.displayName = 'FieldRenderer';
export default FieldRenderer;